import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditableSection from '../../components/EditableSection';
import ReorderableList, { ReorderableItem } from '../../components/ReorderableList';
import SelectField from '../../components/ui/SelectField';
import useEditableDraft from '../../hooks/useEditableDraft';
import { errorMessageFrom } from '../../utils/errorMessage';

export interface OrderScope {
    value: string;
    label: string;
    /** The scope's movable rows, in their saved order. */
    items: ReorderableItem[];
    /** Rows shown first and never moved (the standard category). */
    pinned?: ReorderableItem[];
}

interface ManualOrderSectionProps {
    id: string;
    title: string;
    description: string;
    /** Label of the scope picker (use case / product category / location). */
    scopeLabel: string;
    scopes: OrderScope[];
    readOnly: boolean;
    isLoading: boolean;
    isError: boolean;
    loadErrorText: string;
    saveErrorText: string;
    /** Stores the whole order of one scope; rejects on failure. */
    save: (scope: string, ids: string[]) => Promise<unknown>;
}

/**
 * One "arrange the order" unit (#3016): pick the scope, see its saved order,
 * "Bearbeiten" to drag or move rows, "Speichern" once something moved. Nothing
 * is stored on a drop — the EditableSection rule (design-decisions.md ›
 * Bearbeiten und Speichern).
 */
const ManualOrderSection = ({
    id, title, description, scopeLabel, scopes, readOnly, isLoading, isError,
    loadErrorText, saveErrorText, save,
}: ManualOrderSectionProps) => {
    const { t } = useTranslation();
    const [picked, setPicked] = useState('');
    const scope = scopes.find((s) => s.value === picked) ?? scopes[0] ?? null;
    const savedOrder = useMemo(() => (scope ? scope.items.map((item) => item.id) : []), [scope]);
    const { editing, value, isDirty, startEdit, cancel, finish, setDraft } = useEditableDraft(savedOrder);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // A different scope is a different list: an open draft never carries over.
    // The picker is locked while editing, so this only fires when the scope
    // itself disappears (a refetch, a tenant switch).
    const scopeValue = scope?.value ?? '';
    useEffect(() => {
        cancel();
        setError(null);
    }, [scopeValue, cancel]);

    const onSave = async () => {
        if (!scope) return;
        setSaving(true);
        setError(null);
        try {
            await save(scope.value, value);
            finish();
        } catch (err) {
            setError(errorMessageFrom(err, saveErrorText, t));
        } finally {
            setSaving(false);
        }
    };

    const onCancel = () => {
        setError(null);
        cancel();
    };

    let body;
    if (isLoading) {
        body = (
            <div className={'flex justify-center py-6'}>
                <div className={'animate-spin rounded-full h-8 w-8 border-b-2 border-primary'} />
            </div>
        );
    } else if (isError) {
        // An error is never rendered as an empty list.
        body = <p className={'text-sm text-foreground/60'}>{loadErrorText}</p>;
    } else if (!scope) {
        body = <p className={'text-sm text-foreground/40 italic'}>{t('services.manual_order.nothing_to_order')}</p>;
    } else {
        body = (
            <div className={'flex flex-col gap-3'}>
                {scopes.length > 1 && (
                    <SelectField
                        label={scopeLabel}
                        labelAbove={true}
                        value={scope.value}
                        options={scopes.map((s) => s.value)}
                        optionLabels={Object.fromEntries(scopes.map((s) => [s.value, s.label]))}
                        onChange={setPicked}
                        disabled={editing}
                        className={'max-w-sm'}
                    />
                )}
                {scopes.length === 1 && (
                    <p className={'text-sm text-foreground/60'}>{scopeLabel}: {scope.label}</p>
                )}
                <ReorderableList
                    label={`${title}: ${scope.label}`}
                    items={scope.items}
                    pinned={scope.pinned}
                    order={value}
                    editing={editing}
                    onChange={setDraft}
                />
            </div>
        );
    }

    return (
        <EditableSection
            id={id}
            title={title}
            description={description}
            editing={editing}
            readOnly={readOnly || isLoading || isError || !scope}
            isDirty={isDirty}
            saving={saving}
            error={error}
            onEdit={startEdit}
            onCancel={onCancel}
            onSave={onSave}
            view={body}
        >
            {editing && (
                <p className={'text-sm text-foreground/60'}>{t('services.manual_order.edit_hint')}</p>
            )}
            {body}
        </EditableSection>
    );
};

export default ManualOrderSection;
